/**
 * 工单列表：`GET /api/provider/bookings?status=&from=&to=&page=`
 *
 * ══ 本页的三条硬约束 ══
 *
 * ① **列表展示工单对象的全部 10 个键**：id / status / statusLabel / member /
 *    service / memberNote / createdAt / updatedAt / timeline。
 *    少显示一个键，服务方就得靠别的方式去猜（例如「这条有没有备注」）。
 *
 * ② **显式展示 `dataPolicy`**（接口下发的 purpose / providedFields / withheld）。
 *    这是让服务方**自己看到**「平台给了什么、没给什么」的地方 ——
 *    它比任何一份外部文档都更接近事实，因为它是接口当场说的。
 *
 * ③ **没有「按成员检索」、没有「导出」**：接口不存在这两个能力，
 *    界面因此也没有对应入口。筛选只有状态与服务时间区间两种。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { bookingStatuses, bookingStatusLabels } from '@hew3/shared';
import { providerApi } from '../lib/api.ts';
import type { BookingsResult } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { bookingStatusText, bookingStatusTone, formatDateTime, formatNumber, formatText, slotRangeText } from '../lib/format.ts';
import { Badge, Card, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';

/** 状态筛选：全部 + 平台状态机里的每一个取值（中文名取自共享口径，不另写一份）。 */
const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: '全部' },
  ...bookingStatuses.map((s) => ({ key: s as string, label: bookingStatusLabels[s] })),
];

export function BookingsPage() {
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const list = useLoad<BookingsResult>(
    (signal) => providerApi.bookings({ status, from, to, page }, signal),
    [status, from, to, page],
  );

  const changeFilter = (next: { status?: string; from?: string; to?: string }): void => {
    if (next.status !== undefined) setStatus(next.status);
    if (next.from !== undefined) setFrom(next.from);
    if (next.to !== undefined) setTo(next.to);
    // 筛选一变必须回到第 1 页：停在第 3 页会让「命中 2 条」的结果页显示空表
    setPage(1);
  };

  const data = list.data;
  const policy = data?.dataPolicy;

  return (
    <>
      <PageHead
        title="工单列表"
        sub="BOOKINGS · 仅本机构绑定主体名下的预约"
        actions={
          <button type="button" className="btn sm" onClick={list.reload} disabled={list.loading}>
            刷新
          </button>
        }
      />

      <Card title="筛选" sub="FILTER">
        <div className="row wrap" style={{ gap: 14, alignItems: 'flex-end' }}>
          <div>
            <div className="field-label">状态</div>
            <div className="chip-row">
              {STATUS_FILTERS.map((item) => (
                <button
                  key={item.key || 'all'}
                  type="button"
                  className={`chip${status === item.key ? ' on' : ''}`}
                  onClick={() => changeFilter({ status: item.key })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ width: 170 }}>
            <div className="field-label">服务时间从</div>
            <input
              className="field-input"
              type="date"
              value={from}
              onChange={(e) => changeFilter({ from: e.target.value })}
            />
          </div>
          <div style={{ width: 170 }}>
            <div className="field-label">服务时间至</div>
            <input className="field-input" type="date" value={to} onChange={(e) => changeFilter({ to: e.target.value })} />
          </div>
          {(status !== '' || from !== '' || to !== '') && (
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setStatus('');
                setFrom('');
                setTo('');
                setPage(1);
              }}
            >
              清除筛选
            </button>
          )}
        </div>
        <div className="field-hint">
          时间筛选按<b>服务时间</b>（号源开始时间）过滤，不是下单时间 —— 排班关心的是「这几天要服务谁」。
        </div>
      </Card>

      {policy !== undefined && (
        <div className="policy-box" data-testid="provider-data-policy">
          <div className="pl-t">接口数据面说明（dataPolicy · 由接口当场下发）</div>
          <div>
            <b>用途：</b>
            {formatText(policy.purpose)}
          </div>
          <div style={{ marginTop: 6 }}>
            <b>本次下发的字段：</b>
          </div>
          <div className="pl-fields">
            {policy.providedFields.map((f) => (
              <span key={f} className="pl-field">
                {f}
              </span>
            ))}
          </div>
          <div>
            <b>未下发的数据：</b>
            {formatText(policy.withheld)}
          </div>
        </div>
      )}

      <div className="sp12" />

      <Card title="工单" sub="BOOKINGS" tight>
        {list.loading && <LoadingBox text="正在读取工单…" />}
        {!list.loading && list.error !== null && <ErrorBox message={errorText(list.error)} onRetry={list.reload} />}
        {!list.loading && list.error === null && data !== null && (
          <>
            {data.items.length === 0 ? (
              <EmptyBox title="当前筛选下没有工单" desc="换个状态或时间区间再看看；本机构只能看到绑定主体名下的预约。" />
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>工单号</th>
                      <th>状态</th>
                      <th>成员</th>
                      <th>联系电话</th>
                      <th>服务项目</th>
                      <th>号源</th>
                      <th>服务时间</th>
                      <th>成员备注</th>
                      <th>创建时间</th>
                      <th>更新时间</th>
                      <th>时间轴</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.id}>
                        <td className="mono-num">{item.id}</td>
                        <td>
                          <Badge tone={bookingStatusTone(item.status)}>{item.statusLabel || bookingStatusText(item.status)}</Badge>
                        </td>
                        <td>
                          <b>{formatText(item.member.name)}</b>
                        </td>
                        <td className="mono-num">{formatText(item.member.phone)}</td>
                        <td>{formatText(item.service.resourceName)}</td>
                        <td>{formatText(item.service.slotLabel)}</td>
                        <td>{slotRangeText(item.service.startAt, item.service.endAt)}</td>
                        <td>{formatText(item.memberNote)}</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td>{formatDateTime(item.updatedAt)}</td>
                        <td className="mono-num">{formatNumber(item.timeline.length)} 条</td>
                        <td>
                          <Link className="link" to={`/bookings/${item.id}`}>
                            详情与履约
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="pager">
              <span>
                共 <span className="mono-num">{formatNumber(data.total)}</span> 条 · 每页{' '}
                <span className="mono-num">{formatNumber(data.pageSize)}</span> 条 · 第{' '}
                <span className="mono-num">{formatNumber(data.page)}</span> 页
              </span>
              <div className="grow" />
              <button
                type="button"
                className="btn sm"
                disabled={data.page <= 1 || list.loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <button
                type="button"
                className="btn sm"
                disabled={!data.hasMore || list.loading}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
              </button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
